import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { BDSProvider, Box, CircularProgress } from '@barracuda-internal/bds-core';
import LoginContainer from './comp/login/LoginContainer';
import WrapperContainer from './comp/wrapper/WrapperContainer';
import { actions as authActions } from './comp/login/authReducer';
import {
  GlobalDashboardContainer,
  AccountsContainer,
  UsersContainer,
  AuditLogContainer,
  BackendsContainer,
  GatewaysContainer,
  ScanLookupContainer,
} from './comp/global';

/**
 * Authenticated shell with routed feature pages.
 * Routes mirror the guardian's /global/* paths so the support SPA
 * reuses the same component tree and peek-mode infrastructure.
 */
const AuthenticatedApp = () => (
  <WrapperContainer>
    <Switch>
      <Route exact path="/global/dashboard" component={GlobalDashboardContainer} />
      <Route exact path="/global/accounts" component={AccountsContainer} />
      <Route exact path="/global/users" component={UsersContainer} />
      <Route exact path="/global/audit" component={AuditLogContainer} />
      <Route exact path="/global/backends" component={BackendsContainer} />
      <Route exact path="/global/gateways" component={GatewaysContainer} />
      <Route exact path="/global/scans" component={ScanLookupContainer} />
      <Redirect to="/global/dashboard" />
    </Switch>
  </WrapperContainer>
);

/**
 * Root App component.
 * On mount: dispatches checkSession().
 *   - Saga hits GET /api/v1/support/login/
 *   - If response has {url}: saga redirects to Azure AD (no render needed)
 *   - If response has {authenticated: true}: store updates → AuthenticatedApp renders
 */
const App = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const isLoading = useSelector((s) => s.auth.isLoading);

  useEffect(() => {
    dispatch(authActions.checkSession());
  }, [dispatch]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginContainer />;
  }

  return (
    <BrowserRouter>
      <Switch>
        <Route path="/" component={AuthenticatedApp} />
      </Switch>
    </BrowserRouter>
  );
};

/**
 * Root component — wraps the app in the BDS theme provider.
 * Exported as the default for index.js to render.
 */
const Root = () => (
  <BDSProvider theme="cudaLight">
    <App />
  </BDSProvider>
);

export default Root;
